import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, Home as HomeIcon, Clock, CheckCircle2, X, Plus, User, MapPin, Wrench, Settings, Camera, DollarSign, Building2, MessageSquare, List, ChevronLeft, ChevronRight } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { ServiceProvider } from './types';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VisitDurationDefaults,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
} from '../../lib/visitDuration';
import { loadVisitDurationDefaults } from '../../lib/visitDurationDefaults';
import { BOOKING_SOURCES, BOOKING_SOURCE_LABELS } from '../../lib/attribution';

interface Visit {
  id: string;
  home_id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  status: 'requested' | 'proposed' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_date: string | null;
  estimated_duration_minutes: number;
  actual_start_time: string | null;
  actual_end_time: string | null;
  assigned_to: string | null;
  booked_by: string | null;
  booking_source: 'admin_manual' | 'homeowner_request' | 'automation' | null;
  service_provider_id: string | null;
  internal_name: string | null;
  visit_notes: string | null;
  homeowner_notes: string | null;
  homeowner_submitted_at: string | null;
  cost: number | null;
  created_at: string;
  // Flattened fields from visits_with_details view
  address: string;
  city: string;
  state: string;
  zip_code: string;
  owner_name: string;
  owner_phone: string;
  home_manager: string;
  service_provider_name: string | null;
  service_provider_phone: string | null;
  service_provider_email: string | null;
  total_tasks: number;
  completed_tasks: number;
  photo_count: number;
  market_name: string | null;
}

interface CreateVisitData {
  home_id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  scheduled_date: string;
  estimated_duration_minutes: number;
  assigned_to: string;
  booked_by: string;
  booking_source: 'admin_manual' | 'homeowner_request' | 'automation';
  internal_name?: string;
  visit_notes?: string;
}

const VisitsTab = () => {
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Default: upcoming only (active pipeline). "All" includes completed, cancelled, no_show. */
  const [visitScope, setVisitScope] = useState<'upcoming' | 'all'>('upcoming');
  const [statusFilter, setStatusFilter] = useState<Visit['status'] | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<Visit['visit_type'] | 'all'>('all');
  const [dateFilter, setDateFilter] = useState<string>('all'); // 'today', 'week', 'month', 'all'
  const [bookedByFilter, setBookedByFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [homes, setHomes] = useState<Array<{ id: string; address: string; city: string; state: string; owner_name: string }>>([]);
  const [admins, setAdmins] = useState<Array<{ id: string; full_name: string }>>([]);
  const [homeSearch, setHomeSearch] = useState('');
  const [showHomeDropdown, setShowHomeDropdown] = useState(false);
  const [selectedHomeIndex, setSelectedHomeIndex] = useState(-1);
  const [createFormData, setCreateFormData] = useState<CreateVisitData>({
    home_id: '',
    visit_type: 'service',
    scheduled_date: '',
    estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE.service,
    assigned_to: '',
    booked_by: '',
    booking_source: 'admin_manual',
    internal_name: '',
    visit_notes: ''
  });
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [visitDurationDefaults, setVisitDurationDefaults] = useState<VisitDurationDefaults>({
    ...DEFAULT_VISIT_DURATION_BY_TYPE,
  });
  
  // Calendar view states
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Initialize view mode from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam === 'calendar' || viewParam === 'list') {
      setViewMode(viewParam);
    }
  }, []);

  useEffect(() => {
    fetchVisits();
    fetchHomes();
    fetchAdmins();
    fetchVisitDurationDefaults();
    void fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const id = data.user?.id || null;
      setCurrentUserId(id);
      // Pre-populate booked_by with the signed-in admin so the common case
      // (an admin scheduling a visit themselves) requires zero extra clicks.
      if (id) {
        setCreateFormData((prev) => ({
          ...prev,
          booked_by: prev.booked_by || id,
        }));
      }
    } catch (err) {
      console.error('Failed to load current user for visit attribution:', err);
    }
  };

  useEffect(() => {
    fetchVisits();
  }, [visitScope, statusFilter, typeFilter, dateFilter, bookedByFilter]);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      
      // Query visits with all necessary joins including market
      let query = supabase
        .from('visits')
        .select(`
          *,
          homes:home_id (
            address,
            city,
            state,
            zip_code,
            market_id,
            markets (
              name
            ),
            profiles:owner_id (
              full_name,
              phone_number,
              home_manager
            )
          ),
          service_providers:service_provider_id (
            company_name,
            phone,
            email
          )
        `);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (visitScope === 'upcoming') {
        query = query.in('status', ['requested', 'proposed', 'scheduled', 'in_progress']);
      }

      if (typeFilter !== 'all') {
        query = query.eq('visit_type', typeFilter);
      }

      // Date filtering
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (dateFilter) {
          case 'pending':
            query = query.is('scheduled_date', null);
            break;
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            query = query.gte('scheduled_date', startDate.toISOString())
                        .lt('scheduled_date', new Date(startDate.getTime() + 24 * 60 * 60 * 1000).toISOString());
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            query = query.gte('scheduled_date', startDate.toISOString());
            break;
          case 'month':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            query = query.gte('scheduled_date', startDate.toISOString());
            break;
        }
      }

      if (bookedByFilter === 'unassigned') {
        query = query.is('booked_by', null);
      } else if (bookedByFilter !== 'all') {
        query = query.eq('booked_by', bookedByFilter);
      }

      const { data, error } = await query.order('scheduled_date', { ascending: true });

      if (error) throw error;
      
      // Fetch task and photo counts separately
      const visitIds = (data || []).map((v: any) => v.id);
      
      // Get task counts
      const { data: tasksData } = await supabase
        .from('visit_tasks')
        .select('visit_id, status')
        .in('visit_id', visitIds);
      
      // Get photo counts
      const { data: photosData } = await supabase
        .from('visit_photos')
        .select('visit_id')
        .in('visit_id', visitIds);
      
      // Calculate counts per visit
      const taskCounts = (tasksData || []).reduce((acc: any, task: any) => {
        if (!acc[task.visit_id]) {
          acc[task.visit_id] = { total: 0, completed: 0 };
        }
        acc[task.visit_id].total++;
        if (task.status === 'completed') {
          acc[task.visit_id].completed++;
        }
        return acc;
      }, {});
      
      const photoCounts = (photosData || []).reduce((acc: any, photo: any) => {
        acc[photo.visit_id] = (acc[photo.visit_id] || 0) + 1;
        return acc;
      }, {});
      
      // Transform the data to match Visit interface
      const transformedVisits = (data || []).map((visit: any) => ({
        ...visit,
        address: visit.homes?.address || '',
        city: visit.homes?.city || '',
        state: visit.homes?.state || '',
        zip_code: visit.homes?.zip_code || '',
        owner_name: visit.homes?.profiles?.full_name || '',
        owner_phone: visit.homes?.profiles?.phone_number || '',
        home_manager: visit.homes?.profiles?.home_manager || '',
        service_provider_name: visit.service_providers?.company_name || null,
        service_provider_phone: visit.service_providers?.phone || null,
        service_provider_email: visit.service_providers?.email || null,
        total_tasks: taskCounts[visit.id]?.total || 0,
        completed_tasks: taskCounts[visit.id]?.completed || 0,
        photo_count: photoCounts[visit.id] || 0,
        market_name: visit.homes?.markets?.name || null
      }));
      
      setVisits(transformedVisits);
    } catch (error: any) {
      console.error('Error fetching visits:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHomes = async () => {
    try {
      const { data, error } = await supabase
        .from('homes')
        .select(`
          id, 
          address, 
          city, 
          state,
          profiles!homes_owner_id_fkey (
            full_name
          )
        `)
        .order('address');

      if (error) throw error;
      
      const homesWithOwner = data?.map(home => ({
        id: home.id,
        address: home.address,
        city: home.city,
        state: home.state,
        owner_name: home.profiles?.full_name || 'Unknown'
      })) || [];
      
      setHomes(homesWithOwner);
    } catch (error: any) {
      console.error('Error fetching homes:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name');

      if (error) throw error;
      setAdmins(data || []);
    } catch (error: any) {
      console.error('Error fetching admins:', error);
    }
  };

  const fetchVisitDurationDefaults = async () => {
    try {
      const defaults = await loadVisitDurationDefaults();
      setVisitDurationDefaults(defaults);
    } catch (error: any) {
      console.error('Error fetching visit duration defaults:', error);
    }
  };

  // Filter homes based on search
  const filteredHomes = homes.filter(home => {
    if (!homeSearch.trim()) return true;
    const searchLower = homeSearch.toLowerCase();
    const addressMatch = home.address.toLowerCase().includes(searchLower);
    const cityMatch = home.city.toLowerCase().includes(searchLower);
    const stateMatch = home.state.toLowerCase().includes(searchLower);
    const ownerMatch = home.owner_name.toLowerCase().includes(searchLower);
    return addressMatch || cityMatch || stateMatch || ownerMatch;
  });

  const handleHomeSelect = (homeId: string) => {
    const selectedHome = homes.find(h => h.id === homeId);
    if (selectedHome) {
      setCreateFormData({ ...createFormData, home_id: homeId });
      setHomeSearch(`${selectedHome.address}, ${selectedHome.city}, ${selectedHome.state} - ${selectedHome.owner_name}`);
      setShowHomeDropdown(false);
    }
  };

  const handleCreateVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const visitData: any = {
        home_id: createFormData.home_id,
        visit_type: createFormData.visit_type,
        scheduled_date: createFormData.scheduled_date 
          ? new Date(createFormData.scheduled_date).toISOString() 
          : null,
        estimated_duration_minutes: createFormData.estimated_duration_minutes,
        assigned_to: createFormData.assigned_to || null,
        booked_by: createFormData.booked_by || user.id,
        booking_source: createFormData.booking_source || 'admin_manual',
        internal_name: createFormData.internal_name?.trim() ? createFormData.internal_name.trim() : null,
        visit_notes: createFormData.visit_notes || null,
        created_by: user.id,
        updated_by: user.id
      };

      const { data, error } = await supabase
        .from('visits')
        .insert([visitData])
        .select()
        .single();

      if (error) throw error;

      setShowCreateModal(false);
      setHomeSearch('');
      setShowHomeDropdown(false);
      setCreateFormData({
        home_id: '',
        visit_type: 'service',
        scheduled_date: '',
        estimated_duration_minutes: visitDurationDefaults.service,
        assigned_to: '',
        booked_by: currentUserId || '',
        booking_source: 'admin_manual',
        internal_name: '',
        visit_notes: ''
      });
      
      fetchVisits(); // Refresh the list
    } catch (error: any) {
      console.error('Error creating visit:', error);
      setError(error.message);
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status: Visit['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'proposed': return 'bg-orange-100 text-orange-800';
      case 'requested': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: Visit['visit_type']) => {
    switch (type) {
      case 'maintenance': return 'bg-purple-100 text-purple-800';
      case 'service': return 'bg-orange-100 text-orange-800';
      case 'walkthrough': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Pending scheduling';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getVisitsForDate = (date: Date) => {
    return visits.filter(visit => {
      if (!visit.scheduled_date) return false;
      const visitDate = new Date(visit.scheduled_date);
      return (
        visitDate.getDate() === date.getDate() &&
        visitDate.getMonth() === date.getMonth() &&
        visitDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const navigateToVisit = (visitId: string, e: React.MouseEvent | React.KeyboardEvent) => {
    const url = `/admin/visit?id=${visitId}`;
    if ((e as React.MouseEvent).metaKey || (e as React.MouseEvent).ctrlKey) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  if (loading) {
    return (
      <div className="px-6 pt-6 pb-8">
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
    <div className="space-y-4 sm:space-y-6 px-3 sm:px-6 pt-4 sm:pt-6 pb-6 sm:pb-8 flex flex-1 min-h-0 flex-col overflow-y-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center space-x-3">
          <Calendar className="w-6 h-6 text-gray-400" />
          <h2 className="text-lg sm:text-xl font-semibold">Visits Management</h2>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* View Mode Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 gap-0.5">
            <button
              onClick={() => {
                setViewMode('list');
                const url = new URL(window.location.href);
                url.searchParams.set('view', 'list');
                window.history.pushState({}, '', url);
              }}
              className={`flex items-center gap-1.5 px-3 sm:px-3.5 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                viewMode === 'list' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => {
                setViewMode('calendar');
                const url = new URL(window.location.href);
                url.searchParams.set('view', 'calendar');
                window.history.pushState({}, '', url);
              }}
              className={`flex items-center gap-1.5 px-3 sm:px-3.5 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                viewMode === 'calendar' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Calendar</span>
            </button>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Schedule Visit</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
        <select
          value={visitScope}
          onChange={(e) => setVisitScope(e.target.value as 'upcoming' | 'all')}
          className="col-span-2 sm:col-span-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm font-medium"
          aria-label="Visit scope"
        >
          <option value="upcoming">Upcoming visits</option>
          <option value="all">All visits</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Visit['status'] | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
        >
          <option value="all">All Status</option>
          <option value="requested">Requested</option>
          <option value="proposed">Proposed</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No Show</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as Visit['visit_type'] | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
        >
          <option value="all">All Types</option>
          <option value="maintenance">Maintenance</option>
          <option value="service">Service</option>
          <option value="walkthrough">Walkthrough</option>
        </select>

        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
        >
          <option value="all">All Dates</option>
          <option value="pending">Pending Scheduling</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>

        <select
          value={bookedByFilter}
          onChange={(e) => setBookedByFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
        >
          <option value="all">All Bookers</option>
          <option value="unassigned">Not specified</option>
          {admins.map((admin) => (
            <option key={admin.id} value={admin.id}>{admin.full_name}</option>
          ))}
        </select>

        <button
          onClick={fetchVisits}
          disabled={loading}
          className="col-span-2 sm:col-span-1 flex items-center justify-center sm:justify-start space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 sm:p-6 overflow-x-auto">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setCurrentMonth(new Date())}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Today
              </button>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="w-full min-w-[800px]">
            {/* Day headers */}
            <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days grid */}
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
            {(() => {
              const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
              const days = [];
              
              // Empty cells for days before month starts
              for (let i = 0; i < startingDayOfWeek; i++) {
                days.push(
                  <div key={`empty-${i}`} className="min-h-[120px] bg-gray-50 rounded-lg" />
                );
              }
              
              // Days of the month
              for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day);
                const dayVisits = getVisitsForDate(date);
                const isToday = isSameDay(date, new Date());
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                
                days.push(
                  <div
                    key={day}
                    onClick={() => setSelectedDate(date)}
                    className={`min-h-[120px] p-2 rounded-lg border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-black bg-gray-50'
                        : isToday
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      isToday ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {day}
                    </div>
                    <div className="space-y-1">
                      {dayVisits.slice(0, 3).map(visit => (
                        <div
                          key={visit.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToVisit(visit.id, e);
                          }}
                          className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${getStatusColor(visit.status)}`}
                        >
                          <div className="flex items-center gap-1 mb-0.5">
                            <span className={`inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold ${getTypeColor(visit.visit_type)}`}>
                              {visit.visit_type.charAt(0).toUpperCase() + visit.visit_type.slice(1)}
                            </span>
                          </div>
                          <div className="font-medium truncate text-[11px]">{visit.internal_name || visit.address}</div>
                          {visit.internal_name && (
                            <div className="text-[9px] text-gray-600 truncate">{visit.address}</div>
                          )}
                          {visit.market_name && (
                            <div className="text-[9px] text-gray-600 truncate">{visit.market_name}</div>
                          )}
                          <div className="truncate text-[10px]">
                            {visit.scheduled_date && new Date(visit.scheduled_date).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      ))}
                      {dayVisits.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium pl-1">
                          +{dayVisits.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              
              return days;
            })()}
            </div>
          </div>

          {/* Selected Date Details */}
          {selectedDate && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-3">
                Visits on {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h4>
              {(() => {
                const dayVisits = getVisitsForDate(selectedDate);
                if (dayVisits.length === 0) {
                  return (
                    <p className="text-gray-500 text-sm">No visits scheduled for this date.</p>
                  );
                }
                return (
                  <div className="space-y-2">
                    {dayVisits.map(visit => (
                      <div
                        key={visit.id}
                        onClick={(e) => navigateToVisit(visit.id, e)}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span className="font-medium truncate">{visit.internal_name || visit.address}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${getStatusColor(visit.status)}`}>
                              {visit.status.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${getTypeColor(visit.visit_type)}`}>
                              {visit.visit_type}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
                            {visit.internal_name && (
                              <>
                                <span className="truncate">{visit.address}</span>
                                <span className="hidden sm:inline">•</span>
                              </>
                            )}
                            <span>{visit.owner_name}</span>
                            <span className="hidden sm:inline">•</span>
                            <span>{formatTime(visit.scheduled_date)}</span>
                            {visit.market_name && (
                              <>
                                <span className="hidden sm:inline">•</span>
                                <span className="text-gray-500">{visit.market_name}</span>
                              </>
                            )}
                            {visit.assigned_to && (
                              <>
                                <span className="hidden sm:inline">•</span>
                                <span>{visit.assigned_to}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigateToVisit(visit.id, e);
                          }}
                          className="self-end sm:self-auto px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Visits List */}
      {viewMode === 'list' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        {visits.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Visits Found</h3>
            <p className="text-gray-600 mb-4">
              {visitScope === 'upcoming'
                ? 'No upcoming visits match the current filters. Try "All visits" or adjust filters.'
                : 'No visits match the current filters.'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              Schedule First Visit
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visits.map((visit) => (
              <div
                key={visit.id}
                onClick={(e) => navigateToVisit(visit.id, e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigateToVisit(visit.id, e);
                  }
                }}
                tabIndex={0}
                role="button"
                className="p-4 sm:p-6 hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-black/20"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base mb-0.5 truncate">
                      {visit.internal_name || visit.address}
                    </h3>
                    {visit.internal_name && (
                      <p className="text-xs sm:text-sm text-gray-500 mb-1.5 sm:mb-2 truncate">
                        {visit.address}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3 mt-1.5 sm:mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 sm:py-1 rounded-md text-xs ${getTypeColor(visit.visit_type)}`}>
                        {visit.visit_type}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 sm:py-1 rounded-md text-xs ${getStatusColor(visit.status)}`}>
                        {visit.status.replace('_', ' ')}
                      </span>
                      {!visit.scheduled_date && (
                        <span className="inline-flex items-center px-2 py-0.5 sm:py-1 rounded-md text-xs bg-amber-100 text-amber-800">
                          Pending Date
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1 text-xs sm:text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                        <span>{visit.city}, {visit.state}</span>
                      </div>
                      {visit.market_name && (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                          <span className="text-gray-500">{visit.market_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 min-w-0">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                        <span>{visit.owner_name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                        <span>{formatDate(visit.scheduled_date)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1 text-xs sm:text-sm text-gray-600">
                      {visit.assigned_to && (
                        <div className="flex items-center gap-1.5">
                          <Wrench className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                          <span>
                            {visit.assigned_to === 'internal' ? 'Internal Team' : visit.assigned_to}
                          </span>
                        </div>
                      )}

                      {visit.service_provider_name && (
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                          <span>{visit.service_provider_name}</span>
                          {visit.service_provider_phone && (
                            <span className="hidden sm:inline text-gray-500">• {visit.service_provider_phone}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                        <span>{visit.completed_tasks}/{visit.total_tasks} tasks</span>
                      </div>

                      {visit.photo_count > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Camera className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                          <span>{visit.photo_count} photos</span>
                        </div>
                      )}

                      {visit.cost && (
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-gray-400" />
                          <span>${visit.cost.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {visit.homeowner_notes && (
                      <div className="text-xs sm:text-sm text-blue-600 mt-3 bg-blue-50 p-2 rounded">
                        <div className="flex items-center gap-1.5 mb-1">
                          <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="font-medium">Homeowner Notes:</span>
                        </div>
                        <p className="italic">"{visit.homeowner_notes}"</p>
                        {visit.homeowner_submitted_at && (
                          <p className="text-xs text-blue-500 mt-1">
                            Submitted {new Date(visit.homeowner_submitted_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}

                    {visit.visit_notes && (
                      <p className="text-xs sm:text-sm text-gray-600 mt-3 italic">
                        <span className="font-medium">Internal Notes:</span> "{visit.visit_notes}"
                      </p>
                    )}
                  </div>

                </div>

                {(visit.actual_start_time || visit.actual_end_time) && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 pt-2 border-t border-gray-100">
                    {visit.actual_start_time && (
                      <span>Started: {formatTime(visit.actual_start_time)}</span>
                    )}
                    {visit.actual_end_time && (
                      <span>Ended: {formatTime(visit.actual_end_time)}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Create Visit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-xl sm:rounded-lg p-4 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Schedule New Visit</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setHomeSearch('');
                  setShowHomeDropdown(false);
                  setCreateFormData({
                    home_id: '',
                    visit_type: 'service',
                    scheduled_date: '',
                    estimated_duration_minutes: visitDurationDefaults.service,
                    assigned_to: '',
                    booked_by: currentUserId || '',
                    booking_source: 'admin_manual',
                    internal_name: '',
                    visit_notes: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateVisit} className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Home <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={homeSearch}
                    onChange={(e) => {
                      setHomeSearch(e.target.value);
                      setShowHomeDropdown(true);
                      setSelectedHomeIndex(-1);
                    }}
                    onFocus={() => setShowHomeDropdown(true)}
                    onBlur={() => {
                      setTimeout(() => setShowHomeDropdown(false), 200);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setSelectedHomeIndex(prev => 
                          prev < filteredHomes.length - 1 ? prev + 1 : prev
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSelectedHomeIndex(prev => prev > 0 ? prev - 1 : -1);
                      } else if (e.key === 'Enter' && selectedHomeIndex >= 0) {
                        e.preventDefault();
                        const home = filteredHomes[selectedHomeIndex];
                        handleHomeSelect(home.id);
                      } else if (e.key === 'Escape') {
                        setShowHomeDropdown(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    placeholder="Search by address, city, state, or owner..."
                    required
                  />
                  {homeSearch && createFormData.home_id && (
                    <button
                      type="button"
                      onClick={() => {
                        setHomeSearch('');
                        setCreateFormData({ ...createFormData, home_id: '' });
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {showHomeDropdown && filteredHomes.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredHomes.map((home, index) => {
                        const displayText = `${home.address}, ${home.city}, ${home.state} - ${home.owner_name}`;
                        return (
                          <button
                            key={home.id}
                            type="button"
                            onClick={() => handleHomeSelect(home.id)}
                            className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                              index === selectedHomeIndex ? 'bg-gray-100' : ''
                            }`}
                          >
                            <div className="font-medium">{home.address}</div>
                            <div className="text-sm text-gray-500">
                              {home.city}, {home.state} - {home.owner_name}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {createFormData.home_id && (
                  <div className="mt-1 text-xs text-green-600">
                    Selected: {homes.find(h => h.id === createFormData.home_id)?.address}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Name <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={createFormData.internal_name ?? ''}
                  maxLength={120}
                  onChange={(e) => setCreateFormData({ ...createFormData, internal_name: e.target.value })}
                  placeholder="e.g. Smith — Furnace tune-up"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Helps distinguish similar visits in admin lists. Not shown to homeowners or contractors.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Type
                </label>
                <select
                  value={createFormData.visit_type}
                  onChange={(e) => {
                    const visitType = e.target.value as 'maintenance' | 'service' | 'walkthrough';
                    setCreateFormData({
                      ...createFormData,
                      visit_type: visitType,
                      estimated_duration_minutes: visitDurationDefaults[visitType],
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="service">Service Visit</option>
                  <option value="maintenance">Maintenance Visit</option>
                  <option value="walkthrough">Walkthrough</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Date & Time (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={createFormData.scheduled_date}
                  onChange={(e) => setCreateFormData({ ...createFormData, scheduled_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for visit requests pending homeowner confirmation
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration
                </label>
                <select
                  value={createFormData.estimated_duration_minutes}
                  onChange={(e) =>
                    setCreateFormData({
                      ...createFormData,
                      estimated_duration_minutes: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {formatVisitDurationLabel(minutes)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <select
                  value={createFormData.assigned_to}
                  onChange={(e) => setCreateFormData({ ...createFormData, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="">Unassigned</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.full_name}>
                      {admin.full_name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Who will perform the visit.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booked By
                  </label>
                  <select
                    value={createFormData.booked_by}
                    onChange={(e) =>
                      setCreateFormData({ ...createFormData, booked_by: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="">Not specified</option>
                    {admins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.full_name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Team member who scheduled this visit. Used for performance reporting.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking Source
                  </label>
                  <select
                    value={createFormData.booking_source}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        booking_source: e.target.value as CreateVisitData['booking_source'],
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    {BOOKING_SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {BOOKING_SOURCE_LABELS[source]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Notes (Optional)
                </label>
                <textarea
                  value={createFormData.visit_notes}
                  onChange={(e) => setCreateFormData({ ...createFormData, visit_notes: e.target.value })}
                  rows={3}
                  placeholder="Special instructions or notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Scheduling...' : 'Schedule Visit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default VisitsTab;
